import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import dayjs from '~/timeInit';

import { Download as DownloadIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';

import { useGetParityTrendRangeSummary } from '~/hooks/apis/price-parity/useGetParityTrendRangeSummary';
import { useGetParityTrendSeries } from '~/hooks/apis/price-parity/useGetParityTrendSeries';

import ChartExportLegend from './ChartExportLegend';
import DateRangeSlider from './DateRangeSlider';
import { CARD_SX, PARITY_FILTER_CHIP_BASE_SX, PARITY_SECTION_BLOCK_MB, SECTION_SX } from './constants';
import { downloadDomElementAsPng } from './downloadDomElementAsPng';
import { buildCalendarDatesInclusive, getParityTrendFullWindowStart } from './parityTrendDateRange';
import {
  type ParityTrendMetric,
  buildParityTrendWideRows,
  chipStyleForRegion,
  filterParityTrendDailyByDateRange,
  regionTrendColor,
} from './parityTrendUtils';
import { fmtShortDate } from './utils';

interface Props {
  offerBk: string;
}

export default function ParityTrendSection({ offerBk }: Props) {
  // Snapshot "today" and the FY-aligned window at mount; no refresh if the session crosses midnight.
  const { calendarDates, initialRange } = useMemo(() => {
    const end = dayjs().startOf('day');
    const start = getParityTrendFullWindowStart(end);
    const dates = buildCalendarDatesInclusive(start, end);
    const len = dates.length;
    const range: [number, number] = len > 1 ? [0, len - 1] : [0, 0];
    return { calendarDates: dates, initialRange: range };
  }, []);

  const [rangeIndices, setRangeIndices] = useState<[number, number]>(initialRange);
  const [committedIndices, setCommittedIndices] = useState<[number, number]>(initialRange);

  const fullDateFrom = calendarDates[0] ?? '';
  const fullDateTo = calendarDates[calendarDates.length - 1] ?? '';

  const dateFrom = calendarDates[committedIndices[0]] ?? '';
  const dateTo = calendarDates[committedIndices[1]] ?? '';

  const [metric, setMetric] = useState<ParityTrendMetric>('pct_overpriced');
  const [regionVisible, setRegionVisible] = useState<Record<string, boolean>>({});

  const {
    data: seriesData,
    isLoading: isSeriesLoading,
    isError: isSeriesError,
    error: seriesError,
  } = useGetParityTrendSeries(offerBk, fullDateFrom, fullDateTo);

  const {
    data: summaryData,
    isFetching: isSummaryFetching,
    isError: isSummaryError,
    error: summaryError,
  } = useGetParityTrendRangeSummary(offerBk, dateFrom, dateTo);

  const dailyTrendForRange = useMemo(
    () => filterParityTrendDailyByDateRange(seriesData?.daily_trend ?? [], dateFrom, dateTo),
    [seriesData?.daily_trend, dateFrom, dateTo],
  );

  const { rows, regions } = useMemo(
    () => buildParityTrendWideRows(dailyTrendForRange, metric),
    [dailyTrendForRange, metric],
  );

  const visibleRegions = useMemo(() => regions.filter((r) => regionVisible[r] ?? true), [regions, regionVisible]);

  useEffect(() => {
    if (regions.length === 0) {
      return;
    }
    setRegionVisible((prev) => {
      const next: Record<string, boolean> = {};
      for (const r of regions) {
        next[r] = prev[r] ?? true;
      }
      return next;
    });
  }, [regions]);

  const toggleRegion = (region: string) => {
    setRegionVisible((prev) => {
      const current = prev[region] ?? true;
      return { ...prev, [region]: !current };
    });
  };

  const avgDiffLabel = useMemo(() => {
    const v = summaryData?.summary?.avg_price_diff_when_overpriced;
    if (v == null) {
      return '—';
    }
    return `${(v * 100).toFixed(1)}%`;
  }, [summaryData?.summary?.avg_price_diff_when_overpriced]);

  const errorMessage = useMemo(() => {
    const parts: Array<string> = [];
    if (isSeriesError) {
      parts.push(seriesError instanceof Error ? seriesError.message : String(seriesError ?? ''));
    }
    if (isSummaryError) {
      parts.push(summaryError instanceof Error ? summaryError.message : String(summaryError ?? ''));
    }
    return parts.filter(Boolean).join(' ');
  }, [isSeriesError, isSummaryError, seriesError, summaryError]);

  const handleRangeCommit = (range: Array<number>) => {
    const tuple = [range[0], range[1]] as [number, number];
    setRangeIndices(tuple);
    setCommittedIndices(tuple);
  };

  const chartSectionRef = useRef<HTMLDivElement>(null);
  const [showLegendForExport, setShowLegendForExport] = useState(false);

  const canDownloadChart = !isSeriesLoading && !isSeriesError && rows.length > 0;

  const handleDownloadChart = useCallback(async () => {
    if (!chartSectionRef.current || !canDownloadChart) {
      return;
    }
    // Legend is hidden in the UI but toggled on for capture; downloadDomElementAsPng waits two
    // animation frames before html2canvas so React can commit and layout can settle.
    setShowLegendForExport(true);
    try {
      const safeBk = offerBk.replace(/[^a-zA-Z0-9]/g, '_');
      await downloadDomElementAsPng(
        chartSectionRef.current,
        `overpriced_searches_trend_${safeBk}_${dateFrom}_${dateTo}.png`,
      );
    } finally {
      setShowLegendForExport(false);
    }
  }, [canDownloadChart, dateFrom, dateTo, offerBk]);

  return (
    <Paper elevation={0} sx={SECTION_SX}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: PARITY_SECTION_BLOCK_MB,
        }}
      >
        <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Overpriced searches over time</Typography>
        <IconButton
          onClick={handleDownloadChart}
          title="Download chart as PNG"
          disabled={!canDownloadChart}
          aria-label="Download chart as PNG"
        >
          <DownloadIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
          mb: PARITY_SECTION_BLOCK_MB,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 220, flexShrink: 0 }}>
          <InputLabel id="parity-trend-metric-label">Metric</InputLabel>
          <Select
            labelId="parity-trend-metric-label"
            label="Metric"
            value={metric}
            onChange={(e) => setMetric(e.target.value as ParityTrendMetric)}
          >
            <MenuItem value="pct_overpriced">Overpriced percentage</MenuItem>
            <MenuItem value="overpriced_searches">Overpriced searches</MenuItem>
          </Select>
        </FormControl>

        {regions.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>Regions</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {regions.map((region) => {
                const colors = chipStyleForRegion(region, regions);
                const active = regionVisible[region] ?? true;
                return (
                  <Box
                    key={region}
                    component="button"
                    type="button"
                    onClick={() => toggleRegion(region)}
                    sx={{
                      ...PARITY_FILTER_CHIP_BASE_SX,
                      borderColor: colors.border,
                      color: colors.color,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      bgcolor: active ? colors.bg : 'transparent',
                      opacity: active ? 1 : 0.45,
                    }}
                  >
                    {region}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'flex-start' } }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {(isSeriesError || isSummaryError) && errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box sx={{ position: 'relative', minHeight: 360 }}>
            {isSeriesLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            )}
            {!isSeriesLoading && !isSeriesError && rows.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 4 }}>
                No trend data for this date range.
              </Typography>
            )}
            {!isSeriesLoading && rows.length > 0 && (
              <Box ref={chartSectionRef}>
                <ResponsiveContainer width="100%" height={360}>
                  <LineChart data={rows}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="report_date"
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                      tickFormatter={(v) => fmtShortDate(String(v))}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) =>
                        metric === 'pct_overpriced' ? `${Math.round(Number(v))}%` : String(Math.round(Number(v)))
                      }
                    />
                    <Tooltip
                      labelFormatter={(label) => fmtShortDate(String(label))}
                      formatter={(value: number | string) => {
                        if (value == null || value === '') {
                          return '—';
                        }
                        return metric === 'pct_overpriced' ? `${Number(value).toFixed(1)}%` : String(value);
                      }}
                    />
                    {visibleRegions.map((region) => (
                      <Line
                        key={region}
                        type="monotone"
                        dataKey={region}
                        name={region}
                        stroke={regionTrendColor(region, regions)}
                        strokeWidth={2}
                        dot={false}
                        connectNulls={false}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
                {showLegendForExport && (
                  <ChartExportLegend
                    items={visibleRegions.map((region) => ({
                      label: region,
                      color: regionTrendColor(region, regions),
                    }))}
                  />
                )}
              </Box>
            )}
          </Box>

          {calendarDates.length > 1 && (
            <Box data-html2canvas-ignore>
              <DateRangeSlider
                dates={calendarDates}
                range={rangeIndices}
                onRangeChange={(r) => setRangeIndices(r as [number, number])}
                onRangeCommit={handleRangeCommit}
                showQuickSelects
              />
            </Box>
          )}
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: 320 },
            flexShrink: 0,
          }}
        >
          {isSummaryFetching ? (
            <Box
              sx={{
                ...CARD_SX,
                p: 3,
                minHeight: 160,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box sx={{ ...CARD_SX, p: 3 }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1, fontWeight: 500 }}>
                Avg price difference when overpriced
              </Typography>
              <Typography sx={{ fontSize: 32, fontWeight: 700, mb: 0.5, lineHeight: 1.2 }}>{avgDiffLabel}</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Weighted average for selected range</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
